import { useState } from 'react';
import { generateId, rollFormula, type InitiativeRow } from '@gm-binder/shared';
import { PageHeader, Button, IconButton, Select, EmptyState } from '../ui';
import { useBinder } from '../../lib/useBinder';
import { useRunStore } from '../../store/runStore';
import shared from './sections.module.css';
import styles from './InitiativeSection.module.css';

/** Initiative tracker: add PCs/monsters, roll, edit HP/AC, advance turns/rounds. */
export function InitiativeSection() {
  const ctx = useBinder();
  const setInitiative = useRunStore((s) => s.setInitiative);
  const setRound = useRunStore((s) => s.setInitiativeRound);
  const setActive = useRunStore((s) => s.setInitiativeActive);
  const [monsterPick, setMonsterPick] = useState('');
  if (!ctx) return null;
  const { adventure, state } = ctx;
  const rows = state.initiative;
  const round = state.initiativeRound;
  const active = state.initiativeActive;

  const update = (next: InitiativeRow[]) => setInitiative(next);

  const patchRow = (id: string, patch: Partial<InitiativeRow>) =>
    update(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addPcs = () => {
    const existing = new Set(rows.map((r) => r.ref));
    const activePcs = adventure.characters.filter(
      (c) => !state.inactiveCharacters.includes(c.name) && !existing.has(c.name),
    );
    const newRows: InitiativeRow[] = activePcs.map((c) => ({
      id: generateId('init'),
      name: c.name,
      kind: 'pc',
      init: null,
      hp: state.hp[c.name] ?? c.hitPoints?.current ?? c.hitPoints?.max ?? null,
      maxHp: c.hitPoints?.max ?? null,
      ac: c.armorClass ?? null,
      conditions: [],
      ref: c.name,
    }));
    update([...rows, ...newRows]);
  };

  const addMonster = (id: string) => {
    const m = adventure.monsters.find((x) => x.id === id);
    if (!m) return;
    update([
      ...rows,
      {
        id: generateId('init'),
        name: m.name,
        kind: 'monster',
        init: null,
        hp: Number(m.hp) || null,
        maxHp: Number(m.hp) || null,
        ac: m.ac ?? null,
        conditions: [],
        ref: m.id,
      },
    ]);
  };

  const addBlank = () =>
    update([
      ...rows,
      { id: generateId('init'), name: 'Nuovo', kind: 'custom', init: null, conditions: [] },
    ]);

  const rollAll = () =>
    update(rows.map((r) => (r.init == null ? { ...r, init: rollFormula('1d20').total } : r)));

  const sortByInit = () => {
    const sorted = [...rows].sort((a, b) => (b.init ?? -99) - (a.init ?? -99));
    update(sorted);
    setActive(0);
  };

  const nextTurn = () => {
    if (!rows.length) return;
    let next = active + 1;
    if (next >= rows.length) {
      next = 0;
      setRound(round + 1);
    }
    setActive(next);
  };

  const resetAll = () => {
    update([]);
    setRound(1);
    setActive(0);
  };

  if (!rows.length) {
    return (
      <div>
        <PageHeader eyebrow="Iniziativa" title="Tracker Iniziativa" />
        <div className={shared.toolbar}>
          <Button variant="primary" icon="🛡️" onClick={addPcs}>
            Aggiungi PG attivi
          </Button>
          <Button icon="➕" onClick={addBlank}>
            Riga vuota
          </Button>
        </div>
        <EmptyState
          icon="⏱️"
          title="Combattimento non iniziato"
          description="Aggiungi i personaggi giocanti o dei mostri per cominciare a tracciare l'iniziativa."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Iniziativa"
        title="Tracker Iniziativa"
        subtitle={`Round ${round}`}
        actions={
          <>
            <Button onClick={rollAll} icon="🎲">
              Tira mancanti
            </Button>
            <Button onClick={sortByInit} icon="⬇️">
              Ordina
            </Button>
            <Button variant="primary" onClick={nextTurn} icon="⏭️">
              Turno successivo
            </Button>
          </>
        }
      />

      <div className={shared.toolbar}>
        <Button size="sm" icon="🛡️" onClick={addPcs}>
          PG attivi
        </Button>
        <Select
          value={monsterPick}
          onChange={(e) => {
            if (e.target.value) {
              addMonster(e.target.value);
              setMonsterPick('');
            }
          }}
          style={{ width: 'auto' }}
          aria-label="Aggiungi mostro"
        >
          <option value="">+ Aggiungi mostro…</option>
          {adventure.monsters.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Select>
        <Button size="sm" icon="➕" onClick={addBlank}>
          Riga vuota
        </Button>
        <span className={shared.toolbarSpacer} />
        <Button size="sm" variant="danger" onClick={resetAll}>
          Reset
        </Button>
      </div>

      <div className={styles.table}>
        <div className={[styles.row, styles.head].join(' ')}>
          <span>Init</span>
          <span>Nome</span>
          <span>PF</span>
          <span>CA</span>
          <span>Stati</span>
          <span />
        </div>
        {rows.map((r, i) => (
          <div
            key={r.id}
            className={[styles.row, i === active ? styles.active : undefined]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              className={styles.init}
              type="number"
              value={r.init ?? ''}
              onChange={(e) => patchRow(r.id, { init: e.target.value === '' ? null : Number(e.target.value) })}
              aria-label={`Iniziativa ${r.name}`}
            />
            <div className={styles.nameCell}>
              <span className={styles.kindDot} data-kind={r.kind} aria-hidden />
              <input
                className={styles.name}
                value={r.name}
                onChange={(e) => patchRow(r.id, { name: e.target.value })}
                aria-label="Nome"
              />
            </div>
            <div className={styles.hpCell}>
              <IconButton label="-1 PF" size="sm" onClick={() => patchRow(r.id, { hp: (r.hp ?? 0) - 1 })}>
                −
              </IconButton>
              <input
                className={styles.hpInput}
                type="number"
                value={r.hp ?? ''}
                onChange={(e) => patchRow(r.id, { hp: e.target.value === '' ? null : Number(e.target.value) })}
                aria-label="Punti ferita"
              />
              {r.maxHp != null && <span className={styles.maxHp}>/{r.maxHp}</span>}
              <IconButton label="+1 PF" size="sm" onClick={() => patchRow(r.id, { hp: (r.hp ?? 0) + 1 })}>
                +
              </IconButton>
            </div>
            <input
              className={styles.ac}
              value={r.ac ?? ''}
              onChange={(e) => patchRow(r.id, { ac: e.target.value })}
              aria-label="Classe armatura"
            />
            <input
              className={styles.cond}
              value={r.conditions.join(', ')}
              placeholder="—"
              onChange={(e) =>
                patchRow(r.id, {
                  conditions: e.target.value
                    .split(',')
                    .map((c) => c.trim())
                    .filter(Boolean),
                })
              }
              aria-label="Condizioni"
            />
            <IconButton
              label={`Rimuovi ${r.name}`}
              size="sm"
              onClick={() => update(rows.filter((x) => x.id !== r.id))}
            >
              ✕
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
}
